// Chooses the best garage renderer for the embedded (wizard/results) view:
// the real-time Blender-built 3D garage (lazy three.js chunk) when WebGL is
// available, with the instant SVG scene as loading state and as the permanent
// fallback on devices without WebGL — or when the 3D chunk or its assets fail
// to load (deploy skew, flaky network). A failed chunk must NEVER take down
// the app: the boundary drops us back to the SVG scene for good.
import { Component, Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { GarageCar, bannerLabel, type CarState } from "./GarageCar";
import type { ImpactPoint } from "../shared/questionnaire";

const Garage = lazy(() => import("./garage/Garage"));

export function webglAvailable(): boolean {
  try {
    const c = document.createElement("canvas");
    return Boolean(c.getContext("webgl2") ?? c.getContext("webgl"));
  } catch {
    return false;
  }
}

export class GarageBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// Defer kicking off the three.js fetch until the browser is idle, so the
// critical flow paints first.
export function useIdleReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: 1200 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(t);
  }, []);
  return ready;
}

export function GarageView({
  car,
  onToggleImpact,
}: {
  car: CarState;
  onToggleImpact?: (id: ImpactPoint) => void;
}) {
  const [webgl] = useState(webglAvailable);
  const ready = useIdleReady();
  // The 3D view stays under the SVG until its first frame is drawn, then
  // takes over; an asset failure leaves the SVG in charge.
  const [live, setLive] = useState(false);
  const [failed, setFailed] = useState(false);

  const svg = <GarageCar car={car} />;
  const label = `Your car in a 3D garage${car.insurerName ? `, insured by ${car.insurerName}` : ""}${
    car.impactPoints.length ? `, damage marked: ${car.impactPoints.join(", ")}` : ""
  }`;
  if (!webgl || failed) return svg;
  return (
    <div className={`garage3d-wrap${live ? " live" : ""}`}>
      {!live && <div className="g3d-under">{svg}</div>}
      {ready && (
        <GarageBoundary fallback={null}>
          <Suspense fallback={null}>
            <Garage
              car={car}
              variant="embed"
              onToggleImpact={onToggleImpact}
              onReady={() => setLive(true)}
              onError={() => setFailed(true)}
              label={label}
            />
          </Suspense>
        </GarageBoundary>
      )}
      {/* Visual overlays only — their content is carried by the aria-label
          and the step's own hint text. */}
      {live && bannerLabel(car.insurerName) && (
        <div className="g3d-banner" aria-hidden="true">
          {bannerLabel(car.insurerName)}
        </div>
      )}
      {live && (
        <div className="g3d-hint" aria-hidden="true">
          {onToggleImpact
            ? "Drag to spin · tap a bumper, side, or the roof to mark the hit"
            : "Drag to spin"}
        </div>
      )}
    </div>
  );
}
